import React, { useState, useRef, useEffect } from "react";
import { ChevronDown, Calendar, X } from "lucide-react";

const DateRangeFilter = ({ onRangeChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedRange, setSelectedRange] = useState("Last 30 Days");
  const [showCustomPicker, setShowCustomPicker] = useState(false);
  const [customDates, setCustomDates] = useState({ start: "", end: "" });
  const dropdownRef = useRef(null);

  const ranges = [
    { label: "Last 7 Days", value: "7d" },
    { label: "Last 30 Days", value: "30d" },
    { label: "Monthly", value: "monthly" },
    { label: "Quarterly", value: "quarterly" },
    { label: "Yearly", value: "yearly" },
    { label: "Custom Range", value: "custom" },
  ];

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelect = (range) => {
    if (range.value === "custom") {
      setShowCustomPicker(true);
    } else {
      setSelectedRange(range.label);
      onRangeChange?.(range.value);
      setIsOpen(false);
      setShowCustomPicker(false);
    }
  };

  const handleCustomSubmit = (e) => {
    e.preventDefault();
    if (customDates.start && customDates.end) {
      setSelectedRange(`${customDates.start} - ${customDates.end}`);
      onRangeChange?.("custom", customDates);
      setIsOpen(false);
      setShowCustomPicker(false);
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 bg-white px-4 py-2.5 rounded-xl shadow-sm border border-slate-200 text-sm font-bold text-slate-700 hover:bg-slate-50 transition-all hover:border-primary/30"
      >
        <Calendar size={16} className="text-primary" />
        {selectedRange}
        <ChevronDown size={16} className={`ml-1 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-64 bg-white rounded-2xl shadow-2xl border border-slate-100 z-50 overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200">
          {!showCustomPicker ? (
            <div className="p-2">
              <div className="px-3 py-2 text-[10px] font-black text-slate-400 uppercase tracking-widest">Select Period</div>
              {ranges.map((range) => (
                <button
                  key={range.value}
                  onClick={() => handleSelect(range)}
                  className={`w-full text-left px-3 py-2.5 rounded-xl text-sm font-bold transition-colors ${
                    selectedRange === range.label 
                    ? "bg-primary/5 text-primary" 
                    : "text-slate-600 hover:bg-slate-50"
                  }`}
                >
                  {range.label}
                </button>
              ))}
            </div>
          ) : (
            <div className="p-4">
              <div className="flex justify-between items-center mb-4">
                 <button onClick={() => setShowCustomPicker(false)} className="text-xs font-bold text-primary hover:underline">← Back</button>
                 <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Custom Range</span>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="text-[10px] font-bold text-slate-500 block mb-1 uppercase">Start Date</label>
                  <input
                    type="date"
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none"
                    value={customDates.start}
                    onChange={(e) => setCustomDates({ ...customDates, start: e.target.value })}
                  />
                </div>
                <div>
                  <label className="text-[10px] font-bold text-slate-500 block mb-1 uppercase">End Date</label>
                  <input
                    type="date"
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none"
                    value={customDates.end}
                    onChange={(e) => setCustomDates({ ...customDates, end: e.target.value })}
                  />
                </div>
                <button
                  type="button"
                  onClick={handleCustomSubmit}
                  className="w-full py-2.5 bg-primary text-white text-sm font-bold rounded-xl shadow-lg shadow-primary/20 hover:bg-primary-dark transition-all active:scale-95"
                >
                  Apply Range
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default DateRangeFilter;
